import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, Grid, Container } from "@material-ui/core";
import { Avatar, Button, CssBaseline, TextField } from "@material-ui/core";

// Services
import auth from "../../../../services/auth/auth";

// Components
import AlertComponent from "../../../elements/Alert";
import { DotFalling } from "../../../loader/Loaders";

// Connectors
import backendConnector from "../../../../services/connectors/backendConnector";

// Icons
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";

// Styles
import ForgotPasswordFormStyle from "../../../../assets/styles/materialUI/questionnaire/authentication/forgotPassword/ForgotPasswordFormStyle";

// Providers
import { useLanguage } from "../../../../contexts/languageContext";

const ForgotPasswordForm = ({ isLoading, setIsLoading, error, setError, message, setMessage }) => {
	const history = useHistory();
	const formRef = React.useRef();
	const { t, i18n } = useTranslation();
	const { direction } = useLanguage();
	const classes = ForgotPasswordFormStyle();
	const [count, setCount] = useState(0);
	const [username, setUsername] = useState(null);
	const [emailAddress, setEmailAddress] = useState(null);

	const forgotPassword = async () => {
		if (!formRef.current.reportValidity()) return;
		else if (!auth.isActive()) {
			setError(t("common.licenseExpired"));
		} else {
			setIsLoading(true);
			setError(null);
			let accountDetails = {
				username: username,
				email: emailAddress,
			};
			let response = await backendConnector.forgotPassword(accountDetails);
			if (response?.message) {
				setMessage(response.message);
				setCount(10);
			} else {
				setError(response?.error || "common.errors.passwordResetEmailFailed");
			}
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (count <= 0) return;

		const timer = setInterval(() => {
			setCount((prevCount) => {
				if (prevCount <= 1) {
					clearInterval(timer);
					history.push("/questionnaire/login");
					return 0;
				}
				return prevCount - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [count, history]);

	const translatedMessage = message ? (i18n.exists(message) ? t(message) : message) : null;

	const redirectSignUp = () => {
		history.push("/questionnaire/login");
	};

	return (
		<>
			<Container component="main" maxWidth="xs" dir={direction}>
				<CssBaseline />
				<Grid className={classes.paper}>
					<Avatar className={classes.avatar}>
						<SettingsBackupRestoreIcon />
					</Avatar>
					<Typography variant="h6" color="textSecondary" align="center">
						{t("auth.forgot.title")}
					</Typography>
					<form className={classes.form} ref={formRef}>
						<TextField
							variant="filled"
							margin="normal"
							required
							fullWidth
							id="username"
							label={t("auth.forgot.username")}
							name="username"
							value={username}
							onChange={(e) => setUsername(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") forgotPassword();
							}}
						/>
						<TextField
							variant="filled"
							margin="normal"
							required
							fullWidth
							name="emailAddress"
							label={t("auth.forgot.email")}
							id="emailAddress"
							value={emailAddress}
							onChange={(e) => setEmailAddress(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") forgotPassword();
							}}
						/>
						<Button
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
							onClick={forgotPassword}
						>
							{t("auth.forgot.submit")}
						</Button>
					</form>
				</Grid>
				<Box mt={1} className={classes.normalSeperator}>
					<Typography variant="body2" color="textSecondary" align="center">
						{t("auth.login.or")}
					</Typography>
				</Box>
				<Box mt={1} className={classes.smallSeperator}>
					<Typography variant="body2" color="textSecondary" align="center">
						{t("auth.login.or")}
					</Typography>
				</Box>
				<Button
					fullWidth
					variant="contained"
					color="secondary"
					className={classes.submit}
					onClick={redirectSignUp}
				>
					{t("auth.forgot.back")}
				</Button>
				<Grid className={`${classes.loaderContainer} h-100 w-100 d-flex justify-content-center`}>
					<Grid className={classes.loader}>{isLoading && <DotFalling />}</Grid>
				</Grid>
			</Container>
			{(error || message) && (
				<AlertComponent
					errors={error ? [error] : []}
					messages={
						message
							? [t("auth.signup.redirectCountdown", { message: translatedMessage, seconds: count })]
							: []
					}
					setError={setError}
					setMessage={setMessage}
				/>
			)}
		</>
	);
};

export default ForgotPasswordForm;
